// Phase 4 Fire 1 — back-office Metrics page.
// Extends the existing /admin back-office; reuses Protected adminOnly +
// the same header/nav style. Three MVP counts only — no other metrics.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, LogOut, TrendingUp } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";

export default function BackofficeMetricsPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.backofficeMetrics();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr("Could not load metrics.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[var(--canvas)]" data-testid="metrics-page">
      <BackofficeHeader user={user} onLogout={() => { logout(); nav("/"); }} active="metrics" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">Dashboard</h1>
        <p className="text-slate-500 mb-8 text-sm">
          As of {data?.as_of ? new Date(data.as_of).toLocaleString() : "—"}
        </p>

        {err && <div className="text-sm text-red-600 mb-4" data-testid="metrics-error">{err}</div>}

        {!data && !err && <div className="text-sm text-slate-500">Loading…</div>}

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <MetricCard
              testid="metric-users"
              label="New user registrations"
              primary={data.users.total}
              primary_label="total"
              secondary={data.users.this_month}
              secondary_label="this month"
            />
            <MetricCard
              testid="metric-cases"
              label="Applications started"
              primary={data.applications_started.total}
              primary_label="total"
              secondary={data.applications_started.this_month}
              secondary_label="this month"
            />
            <MetricCard
              testid="metric-incorporated"
              label="Applications finished"
              primary={data.applications_finished.incorporated}
              primary_label="incorporated"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ testid, label, primary, primary_label, secondary, secondary_label }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3">
        <TrendingUp size={12} /> {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-4xl font-semibold text-slate-900" data-testid={`${testid}-primary`}>
          {Number(primary ?? 0).toLocaleString()}
        </span>
        <span className="text-xs text-slate-500">{primary_label}</span>
      </div>
      {secondary !== undefined && (
        <div className="mt-3 text-sm text-slate-600" data-testid={`${testid}-secondary`}>
          <span className="font-semibold text-slate-900">{Number(secondary ?? 0).toLocaleString()}</span> {secondary_label}
        </div>
      )}
    </div>
  );
}

export function BackofficeHeader({ user, onLogout, active }) {
  const linkCls = (k) =>
    `text-xs ${active === k ? "text-slate-900 font-semibold" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2 text-slate-900">
          <Building2 size={22} className="text-[#C5283F]" />
          <span className="font-display text-xl font-semibold">Infin · Back office</span>
        </Link>
        <div className="flex items-center gap-4 text-xs">
          <Link to="/admin" className={linkCls("cases")} data-testid="nav-cases">Cases</Link>
          <Link to="/admin/metrics" className={linkCls("metrics")} data-testid="nav-metrics">Dashboard</Link>
          <Link to="/admin/users" className={linkCls("users")} data-testid="nav-users">Users</Link>
          <Link to="/admin/escalations" className={linkCls("escalations")} data-testid="nav-escalations">Escalations</Link>
          <Link to="/admin/kb" className={linkCls("kb")} data-testid="nav-kb">KB</Link>
          <span className="text-slate-500">{user?.email}</span>
          <button onClick={onLogout}
                  data-testid="admin-logout-btn"
                  className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>
    </div>
  );
}
